import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Link2, Loader2, MapPin, Search, UserCircle, UserCog, X } from 'lucide-react';
import type { LifePeriod, MemoryObject } from '@legacyorb/shared-types';

import {
  TestUserPicker,
  TEST_USERS_QUERY_KEY,
  fetchTestUsers,
  type TestUser,
} from '@/components/interview/TestUserPicker';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth-store';
import { StoryBeatsTab } from '@/components/memory-archive/StoryBeatsTab';
import {
  fetchMemories,
  fetchMemory,
  fetchMemoryCardCounts,
  fetchPeople,
  fetchPlaces,
  fetchTimeline,
  memoriesQueryKey,
  memoryCardCountsQueryKey,
  memoryDetailQueryKey,
  peopleQueryKey,
  placesQueryKey,
  searchMemories,
  timelineQueryKey,
  type EntityRollup,
  type MemorySearchHit,
  type TimelineEntry,
} from '@/services/memory-archive';

// ─── URL state ──────────────────────────────────────────────────────────────

const AS_USER_PARAM = 'asUser';
const TAB_PARAM = 'tab';
const THEMES_PARAM = 'themes';
const ENTITY_PARAM = 'entity';
const QUERY_PARAM = 'q';
const MEMORY_PARAM = 'memory';
const CARD_PARAM = 'card';

type ArchiveTab = 'memories' | 'people-places' | 'timeline' | 'story-beats';
const TAB_VALUES: ArchiveTab[] = ['memories', 'people-places', 'timeline', 'story-beats'];

const LIFE_PERIODS: Array<{ id: LifePeriod; label: string }> = [
  { id: 'childhood', label: 'Childhood' },
  { id: 'education', label: 'Education' },
  { id: 'career', label: 'Career' },
  { id: 'family', label: 'Family' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'health', label: 'Health' },
  { id: 'travel', label: 'Travel' },
  { id: 'military', label: 'Military' },
  { id: 'retirement', label: 'Retirement' },
  { id: 'other', label: 'Other' },
];

// ─── Page ───────────────────────────────────────────────────────────────────

export function MemoryArchive() {
  const [searchParams, setSearchParams] = useSearchParams();

  const targetUserId = searchParams.get(AS_USER_PARAM);
  const tabRaw = searchParams.get(TAB_PARAM);
  const tab: ArchiveTab = TAB_VALUES.includes(tabRaw as ArchiveTab)
    ? (tabRaw as ArchiveTab)
    : 'memories';
  const entity = searchParams.get(ENTITY_PARAM);
  const themesRaw = searchParams.get(THEMES_PARAM);
  const themes = themesRaw
    ? themesRaw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    : [];
  const search = searchParams.get(QUERY_PARAM) ?? '';
  const memoryDrawerId = searchParams.get(MEMORY_PARAM);
  const cardFilter = searchParams.get(CARD_PARAM);

  // Story Beats read the admin User-Insights API, which needs a concrete user
  // id. Use the impersonation target when one is set, else the admin's own id.
  const selfUserId = useAuthStore((s) => s.user?.id ?? null);
  const beatsIdentifier = targetUserId ?? selfUserId;

  // Reuse the picker's shared cache for test-user labels (banner needs it).
  const { data: testUsers } = useQuery<TestUser[]>({
    queryKey: TEST_USERS_QUERY_KEY,
    queryFn: fetchTestUsers,
    staleTime: 60_000,
  });
  const targetUser = useMemo(
    () => testUsers?.find((u) => u.id === targetUserId) ?? null,
    [testUsers, targetUserId]
  );

  const updateParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutator(next);
      // Strip empty values so the URL stays clean.
      for (const key of Array.from(next.keys())) {
        if (next.get(key) === '' || next.get(key) === null) {
          next.delete(key);
        }
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  function handleTargetSelect(user: TestUser | null) {
    updateParams((p) => {
      if (user) p.set(AS_USER_PARAM, user.id);
      else p.delete(AS_USER_PARAM);
    });
  }

  function handleTabChange(value: string) {
    updateParams((p) => p.set(TAB_PARAM, value));
  }

  function handleEntityClick(name: string) {
    updateParams((p) => {
      p.set(TAB_PARAM, 'memories');
      p.set(ENTITY_PARAM, name);
    });
  }

  function handleClearEntity() {
    updateParams((p) => p.delete(ENTITY_PARAM));
  }

  function handleSearchChange(value: string) {
    updateParams((p) => {
      if (value) p.set(QUERY_PARAM, value);
      else p.delete(QUERY_PARAM);
    });
  }

  function handleCardFilterChange(cardUuid: string | null) {
    updateParams((p) => {
      if (cardUuid) p.set(CARD_PARAM, cardUuid);
      else p.delete(CARD_PARAM);
    });
  }

  function handleOpenMemory(id: string) {
    updateParams((p) => p.set(MEMORY_PARAM, id));
  }

  function handleCloseMemory() {
    updateParams((p) => p.delete(MEMORY_PARAM));
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-3">
        <TestUserPicker selectedUserId={targetUserId} onSelect={handleTargetSelect} />
        {targetUserId && (
          <ImpersonationBanner user={targetUser} onExit={() => handleTargetSelect(null)} />
        )}
      </div>

      <header className="border-b pb-4">
        <h1 className="font-display text-3xl font-bold tracking-tight">Memory Archive</h1>
        <p className="text-muted-foreground">
          Browse, search, and explore the memories captured across past sessions.
        </p>
      </header>

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="memories">Memories</TabsTrigger>
          <TabsTrigger value="people-places">People &amp; Places</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="story-beats">Story Beats</TabsTrigger>
        </TabsList>

        <TabsContent value="memories" className="mt-6">
          <MemoriesTab
            targetUserId={targetUserId}
            cardId={cardFilter}
            themes={themes}
            entity={entity}
            search={search}
            onCardChange={handleCardFilterChange}
            onClearEntity={handleClearEntity}
            onSearchChange={handleSearchChange}
            onOpenMemory={handleOpenMemory}
          />
        </TabsContent>

        <TabsContent value="people-places" className="mt-6">
          <PeoplePlacesTab targetUserId={targetUserId} onEntityClick={handleEntityClick} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <TimelineTab targetUserId={targetUserId} onOpenMemory={handleOpenMemory} />
        </TabsContent>

        <TabsContent value="story-beats" className="mt-6">
          <StoryBeatsTab
            identifier={beatsIdentifier}
            cardFilter={cardFilter}
            onCardFilterChange={handleCardFilterChange}
          />
        </TabsContent>
      </Tabs>

      <MemoryDrawer
        targetUserId={targetUserId}
        memoryId={memoryDrawerId}
        open={!!memoryDrawerId}
        onClose={handleCloseMemory}
      />
    </div>
  );
}

// ─── Memories tab ──────────────────────────────────────────────────────────

interface MemoriesTabProps {
  targetUserId: string | null;
  /** story_cards.id of the selected card filter, or null for "All cards". */
  cardId: string | null;
  themes: string[];
  entity: string | null;
  search: string;
  onCardChange: (cardUuid: string | null) => void;
  onClearEntity: () => void;
  onSearchChange: (s: string) => void;
  onOpenMemory: (id: string) => void;
}

function MemoriesTab({
  targetUserId,
  cardId,
  themes,
  entity,
  search,
  onCardChange,
  onClearEntity,
  onSearchChange,
  onOpenMemory,
}: MemoriesTabProps) {
  // Debounce the URL → fetch path so every keystroke doesn't fan out to
  // /memories/search. The debounce target is a local state mirror of the
  // URL value so the input stays responsive while the URL eventually
  // catches up.
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => setSearchDraft(search), [search]);
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (searchDraft !== search) onSearchChange(searchDraft);
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchDraft, search, onSearchChange]);

  const trimmedSearch = search.trim();
  const isSearchMode = trimmedSearch.length > 0;

  // Story-card counts power the left filter. The endpoint returns the full
  // active catalog; we show only cards that actually have memories (count > 0)
  // so the list stays short and meaningful — same behaviour as the Story Beats
  // sidebar (which only lists cards with beats).
  const cardCountsQuery = useQuery({
    queryKey: memoryCardCountsQueryKey(targetUserId),
    queryFn: () => fetchMemoryCardCounts(targetUserId),
    staleTime: 30_000,
  });
  // Defensive: never .map a non-array (a malformed response must not white-
  // screen the Memories tab — degrade to an empty sidebar instead).
  const cardCounts = (Array.isArray(cardCountsQuery.data) ? cardCountsQuery.data : []).filter(
    (c) => c.count > 0
  );

  const listQuery = useQuery({
    queryKey: memoriesQueryKey(targetUserId, { cardId, themes, entity }),
    queryFn: () =>
      fetchMemories(targetUserId, {
        cardId: cardId ?? undefined,
        themes,
        entity: entity ?? undefined,
      }),
    enabled: !isSearchMode,
    staleTime: 30_000,
  });

  const searchQuery = useQuery({
    queryKey: ['memory-archive', 'search', targetUserId ?? 'self', trimmedSearch],
    queryFn: () => searchMemories(targetUserId, trimmedSearch, 10),
    enabled: isSearchMode,
    staleTime: 30_000,
  });

  const items: Array<MemoryObject | MemorySearchHit> = isSearchMode
    ? (searchQuery.data ?? [])
    : (listQuery.data?.items ?? []);

  const isLoading = isSearchMode ? searchQuery.isLoading : listQuery.isLoading;

  return (
    <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
      <aside className="space-y-4">
        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-display text-sm font-semibold">Search</h3>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="Father, summer of 92…"
              aria-label="Search memories"
              className="pl-8"
            />
          </div>
          {isSearchMode && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Showing semantic-search results (top 10).
            </p>
          )}
        </div>

        <div className="rounded-lg border bg-card p-4">
          <h3 className="font-display text-sm font-semibold">Story card</h3>
          <ul className="mt-2 max-h-[60vh] space-y-1 overflow-y-auto text-sm">
            <li>
              <button
                type="button"
                onClick={() => onCardChange(null)}
                className={cn(
                  'w-full rounded px-2 py-1 text-left',
                  cardId === null
                    ? 'bg-orb-gold/15 font-medium text-orb-teal'
                    : 'text-muted-foreground hover:bg-accent'
                )}
              >
                All cards
              </button>
            </li>
            {cardCountsQuery.isLoading && (
              <li className="px-2 py-1 text-xs text-muted-foreground">Loading cards…</li>
            )}
            {!cardCountsQuery.isLoading && cardCounts.length === 0 && (
              <li className="px-2 py-1 text-xs text-muted-foreground">
                No memories tagged to a card yet.
              </li>
            )}
            {cardCounts.map((card) => (
              <li key={card.cardUuid}>
                <button
                  type="button"
                  onClick={() => onCardChange(card.cardUuid)}
                  // Indent by depth so the parent/child hierarchy reads like
                  // the interview coverage panel.
                  style={{ paddingLeft: 8 + (Math.max(1, card.depth) - 1) * 12 }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded py-1 pr-2 text-left',
                    cardId === card.cardUuid
                      ? 'bg-orb-gold/15 font-medium text-orb-teal'
                      : 'text-muted-foreground hover:bg-accent'
                  )}
                  title={card.groupName ? `${card.groupName} · ${card.title}` : card.title}
                >
                  <span className="truncate">{card.title}</span>
                  <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                    ({card.count})
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {entity && (
          <div className="rounded-lg border bg-orb-cream p-4">
            <h3 className="font-display text-sm font-semibold">Entity filter</h3>
            <div className="mt-2 flex items-center justify-between gap-2 text-sm">
              <span className="font-medium">{entity}</span>
              <button
                type="button"
                onClick={onClearEntity}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                aria-label="Clear entity filter"
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            </div>
          </div>
        )}
      </aside>

      <section aria-label="Memory list">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading memories…
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No memories yet"
            body={
              isSearchMode
                ? 'No memories matched that search. Try a different phrase.'
                : 'Once a session completes and is processed, its memories will appear here.'
            }
          />
        ) : (
          <ul className="space-y-3">
            {items.map((m) => (
              <li key={m.id}>
                <MemoryCard
                  memory={m}
                  similarity={'similarity' in m ? m.similarity : undefined}
                  onOpen={() => onOpenMemory(m.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ─── Memory card ────────────────────────────────────────────────────────────

function MemoryCard({
  memory,
  similarity,
  onOpen,
}: {
  memory: MemoryObject;
  similarity?: number;
  onOpen: () => void;
}) {
  const periodLabel =
    LIFE_PERIODS.find((p) => p.id === memory.lifePeriod)?.label ?? memory.lifePeriod;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full rounded-lg border bg-card p-4 text-left transition-colors hover:border-orb-gold hover:bg-orb-cream/50"
      aria-label={`Open memory: ${memory.summary}`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm leading-relaxed text-foreground">{memory.summary}</p>
        {typeof similarity === 'number' && (
          <Badge variant="secondary" className="shrink-0 tabular-nums">
            {(similarity * 100).toFixed(0)}%
          </Badge>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <Badge variant="outline">{periodLabel}</Badge>
        {memory.themes.slice(0, 4).map((t) => (
          <Badge key={t} variant="outline" className="font-normal">
            {t}
          </Badge>
        ))}
        {memory.people.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <UserCircle className="h-3 w-3" />
            {memory.people
              .slice(0, 3)
              .map((p) => p.name)
              .join(', ')}
            {memory.people.length > 3 ? ` +${memory.people.length - 3}` : ''}
          </span>
        )}
        {memory.places.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {memory.places
              .slice(0, 2)
              .map((p) => p.name)
              .join(', ')}
          </span>
        )}
      </div>
    </button>
  );
}

// ─── People & Places tab ────────────────────────────────────────────────────

function PeoplePlacesTab({
  targetUserId,
  onEntityClick,
}: {
  targetUserId: string | null;
  onEntityClick: (name: string) => void;
}) {
  const peopleQ = useQuery({
    queryKey: peopleQueryKey(targetUserId),
    queryFn: () => fetchPeople(targetUserId),
    staleTime: 30_000,
  });
  const placesQ = useQuery({
    queryKey: placesQueryKey(targetUserId),
    queryFn: () => fetchPlaces(targetUserId),
    staleTime: 30_000,
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <EntityColumn
        title="People"
        emptyText="No people referenced yet."
        icon={<UserCircle className="h-4 w-4" />}
        loading={peopleQ.isLoading}
        items={peopleQ.data ?? []}
        onClick={onEntityClick}
      />
      <EntityColumn
        title="Places"
        emptyText="No places referenced yet."
        icon={<MapPin className="h-4 w-4" />}
        loading={placesQ.isLoading}
        items={placesQ.data ?? []}
        onClick={onEntityClick}
      />
    </div>
  );
}

function EntityColumn({
  title,
  emptyText,
  icon,
  loading,
  items,
  onClick,
}: {
  title: string;
  emptyText: string;
  icon: React.ReactNode;
  loading: boolean;
  items: EntityRollup[];
  onClick: (name: string) => void;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        {icon}
        <h3 className="font-display text-base font-semibold">{title}</h3>
        <span className="ml-auto text-xs text-muted-foreground">{items.length}</span>
      </header>
      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">{emptyText}</div>
      ) : (
        <ul className="divide-y">
          {items.map((entity) => (
            <li key={entity.name}>
              <button
                type="button"
                onClick={() => onClick(entity.name)}
                className="block w-full px-4 py-3 text-left hover:bg-accent"
                aria-label={`Filter memories by ${entity.name}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <span className="font-medium">{entity.name}</span>
                    {entity.relationship && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        — {entity.relationship}
                      </span>
                    )}
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {entity.mentionCount} {entity.mentionCount === 1 ? 'mention' : 'mentions'}
                  </span>
                </div>
                <div className="mt-2">
                  <DensityBar value={entity.avgEmotionalDensity} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DensityBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-orb-cream"
      role="progressbar"
      aria-label="Average emotional density"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-full bg-orb-teal" style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Timeline tab ───────────────────────────────────────────────────────────

function TimelineTab({
  targetUserId,
  onOpenMemory,
}: {
  targetUserId: string | null;
  onOpenMemory: (id: string) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: timelineQueryKey(targetUserId),
    queryFn: () => fetchTimeline(targetUserId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading timeline…
      </div>
    );
  }

  const items = data ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="Nothing on the timeline yet"
        body="Events will appear here as the AI extracts them from completed sessions."
      />
    );
  }

  // Group entries by decade (or "Unknown date" bucket). We bucket on the
  // year extracted from the start of the date string — flexible enough for
  // ISO dates ("1995-04-01"), partial dates ("1995"), and natural-language
  // hints ("summer of 1992").
  const grouped = groupByDecade(items);

  return (
    <ol className="relative ml-3 space-y-6 border-l border-border pl-6">
      {grouped.map((group) => (
        <li key={group.label} className="relative">
          <div className="absolute -left-[33px] mt-1 h-3 w-3 rounded-full border-2 border-orb-gold bg-background" />
          <h3 className="font-display text-base font-semibold text-foreground">{group.label}</h3>
          <ul className="mt-3 space-y-3">
            {group.entries.map((entry, idx) => (
              <li key={`${entry.memoryId}-${entry.name}-${idx}`}>
                <button
                  type="button"
                  onClick={() => onOpenMemory(entry.memoryId)}
                  className="block w-full rounded-lg border bg-card p-3 text-left hover:border-orb-gold"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{entry.name}</p>
                      {entry.date && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {entry.date}
                          {entry.endDate ? ` – ${entry.endDate}` : ''}
                        </p>
                      )}
                    </div>
                    <ConfidenceBadge value={entry.confidence} />
                  </div>
                  {entry.significance && (
                    <p className="mt-2 text-xs text-muted-foreground">{entry.significance}</p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function ConfidenceBadge({ value }: { value: 'high' | 'medium' | 'low' }) {
  const cls =
    value === 'high'
      ? 'border-orb-teal/40 bg-orb-teal/10 text-orb-teal'
      : value === 'medium'
        ? 'border-orb-gold/50 bg-orb-gold/10 text-orb-gold'
        : 'border-border bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide',
        cls
      )}
    >
      {value}
    </span>
  );
}

interface DecadeGroup {
  label: string;
  // For sort: Number.POSITIVE_INFINITY puts undated last.
  sortKey: number;
  entries: TimelineEntry[];
}

function groupByDecade(items: TimelineEntry[]): DecadeGroup[] {
  const buckets = new Map<string, DecadeGroup>();
  for (const entry of items) {
    const year = parseYear(entry.date);
    const label = year === null ? 'Unknown date' : `${Math.floor(year / 10) * 10}s`;
    const sortKey = year === null ? Number.POSITIVE_INFINITY : Math.floor(year / 10) * 10;
    let bucket = buckets.get(label);
    if (!bucket) {
      bucket = { label, sortKey, entries: [] };
      buckets.set(label, bucket);
    }
    bucket.entries.push(entry);
  }
  return Array.from(buckets.values()).sort((a, b) => a.sortKey - b.sortKey);
}

function parseYear(raw: string | undefined): number | null {
  if (!raw) return null;
  const match = raw.match(/(\d{4})/);
  if (!match) return null;
  const year = Number.parseInt(match[1], 10);
  if (!Number.isFinite(year)) return null;
  if (year < 1800 || year > 2200) return null;
  return year;
}

// ─── Drawer ─────────────────────────────────────────────────────────────────

function MemoryDrawer({
  targetUserId,
  memoryId,
  open,
  onClose,
}: {
  targetUserId: string | null;
  memoryId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { data: memory, isLoading } = useQuery({
    queryKey: memoryDetailQueryKey(targetUserId, memoryId),
    queryFn: () => fetchMemory(targetUserId, memoryId!),
    enabled: !!memoryId,
    staleTime: 60_000,
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Memory detail</DialogTitle>
          <DialogDescription>
            Captured {memory?.createdAt ? new Date(memory.createdAt).toLocaleDateString() : '—'}
          </DialogDescription>
        </DialogHeader>
        {isLoading || !memory ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <section>
              <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Summary
              </h4>
              <p className="mt-1 leading-relaxed">{memory.summary}</p>
            </section>
            <section>
              <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Raw text
              </h4>
              <p className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-muted/40 p-3 text-xs leading-relaxed">
                {memory.rawText}
              </p>
            </section>
            {/* Story card (MEMORY_CARD_TAGGING). When the memory is tagged to
                a card, show that card's title. Cross-tags ("also tagged → …")
                get room on their own full-width row. When the memory has no
                card (flag off / legacy / untagged), fall back to the memory's
                life-period category — still labelled "Story card" so the field
                reads consistently — so nothing is ever blank. */}
            {(() => {
              const mapped = memory.mappedCards ?? [];
              const primary = mapped.find((c) => c.isPrimary) ?? mapped[0] ?? null;
              const crossTags = mapped.filter((c) => c.cardUuid !== primary?.cardUuid);
              return (
                <>
                  {primary && (
                    <section>
                      <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Story card
                      </h4>
                      <p className="mt-1 font-medium">{primary.title}</p>
                      {crossTags.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <Link2 className="h-3.5 w-3.5 text-orb-teal" aria-hidden="true" />
                          <span className="text-[11px] font-medium text-muted-foreground">
                            also tagged →
                          </span>
                          {crossTags.map((tag) => (
                            <Badge
                              key={tag.cardUuid}
                              className="border-orb-gold/50 bg-orb-gold/10 font-normal tabular-nums text-orb-teal"
                              title={`Also tagged to ${tag.title} with relevance ${tag.relevanceScore.toFixed(2)}`}
                            >
                              {tag.title} ({tag.relevanceScore.toFixed(2)})
                            </Badge>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                  <section className="grid gap-4 sm:grid-cols-2">
                    {!primary && (
                      <div>
                        <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Story card
                        </h4>
                        <p className="mt-1 capitalize">{memory.lifePeriod}</p>
                      </div>
                    )}
                    <div>
                      <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Emotional density
                      </h4>
                      <p className="mt-1 tabular-nums">
                        {Math.round(memory.emotionalDensity * 100)}%
                      </p>
                    </div>
                  </section>
                </>
              );
            })()}
            <section>
              <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Themes
              </h4>
              <div className="mt-1 flex flex-wrap gap-1">
                {memory.themes.length === 0 && (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
                {memory.themes.map((t) => (
                  <Badge key={t} variant="outline">
                    {t}
                  </Badge>
                ))}
              </div>
            </section>
            <section className="grid gap-4 sm:grid-cols-2">
              <div>
                <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  People ({memory.people.length})
                </h4>
                <ul className="mt-1 space-y-0.5">
                  {memory.people.length === 0 && (
                    <li className="text-xs text-muted-foreground">—</li>
                  )}
                  {memory.people.map((p) => (
                    <li key={p.name}>
                      {p.name}
                      {p.relationship && (
                        <span className="text-xs text-muted-foreground"> ({p.relationship})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Places ({memory.places.length})
                </h4>
                <ul className="mt-1 space-y-0.5">
                  {memory.places.length === 0 && (
                    <li className="text-xs text-muted-foreground">—</li>
                  )}
                  {memory.places.map((p) => (
                    <li key={p.name}>
                      {p.name}
                      {p.significance && (
                        <span className="text-xs text-muted-foreground"> · {p.significance}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
            <section>
              {/* PR #2 currently emits one source session per memory; the UI
                  shows whatever the row has. When merge-across-sessions
                  lands we'll list each id with a session link. */}
              <h4 className="font-display text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Source sessions ({memory.sourceSessionIds.length})
              </h4>
              <ul className="mt-1 space-y-0.5 font-mono text-xs">
                {memory.sourceSessionIds.map((id) => (
                  <li key={id} className="truncate">
                    {id}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Bits ───────────────────────────────────────────────────────────────────

function ImpersonationBanner({ user, onExit }: { user: TestUser | null; onExit: () => void }) {
  const name = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-orb-teal/40 bg-orb-teal/10 px-4 py-2 text-sm text-orb-teal"
    >
      <span className="flex items-center gap-2">
        <UserCog className="h-4 w-4" />
        {name ? (
          <>
            Acting as <strong className="font-semibold">{name}</strong>
            {user?.personaName ? (
              <span className="text-orb-teal/80">— {user.personaName}</span>
            ) : null}
          </>
        ) : (
          <span className="text-orb-teal/80">Loading test user…</span>
        )}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onExit}
        className="text-orb-teal hover:bg-orb-teal/20 hover:text-orb-teal"
      >
        Switch / exit
      </Button>
    </div>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
